import { Component, DestroyRef, effect, forwardRef, HostListener, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import dayjs, { Dayjs } from 'dayjs';
import {
	MetadataService,
	TenantPaymentService,
	TenantPaymentValue,
	TenantPaymentValueService,
	TenantSelectionService,
	TenantSelectionValue,
	TenantSelectionValueService,
	TenantSettingService,
	TenantSettingType,
	TenantSettingValue,
	TenantSettingValueService,
	TenantShareService,
	TenantShareValue,
	TenantShareValueService,
	TenantTarifService,
	TenantTarifValue,
	TenantTarifValueService,
	TenantUnitReference,
	TenantUnitReferenceService,
	TenantUnitUserReference,
	TenantUnitUserReferenceService,
} from '@eadsysteme/billingclient-v1';
import { LayoutService } from '../../../../../../../../@vex/services/layout.service';
import { NgClass } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS } from '../../../../../../../util/material-dayjs-adapter';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { SessionService } from '../../../../../../../services/session.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateUtil } from '../../../../../../../util/DateUtil';
import { Observable } from 'rxjs';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { SnackbarService } from '../../../../../../../services/snackbar.service';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmationDialogComponent } from '../../../../../../confirmation-dialog/confirmation-dialog.component';
import { DateValidator } from '../../../../../../../services/DateValidator';
import { ParameterValueType } from '../../../../../../util/ara-bar-chart/gantt.models';
import { DecimalFormatDirective } from '../../../../../../../directives/decimal-format.directive';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

@Component({
	selector: 'ara-parameter-value-dialog',
	imports: [
		FormsModule,
		MatButton,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatError,
		MatFormField,
		MatIcon,
		MatInput,
		MatLabel,
		MatSuffix,
		ReactiveFormsModule,
		TranslocoDirective,
		NgClass,
		MatSelect,
		MatOption,
		MatSelectTrigger,
		MatIconButton,
		MatTooltip,
		MatDatepickerToggleIcon,
		DecimalFormatDirective,
		CdkDrag,
		CdkDragHandle,
	],
	templateUrl: './parameter-value-dialog.component.html',
	styleUrl: './parameter-value-dialog.component.scss',
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: DateUtil.DATE_FORMATS },
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ParameterValueDialogComponent),
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('slideAnimation', [
			// Initial state - content sichtbar
			state(
				'initial',
				style({
					transform: 'translateX(0%)',
					opacity: 1,
				})
			),
			// Animation when sliding out to left (for next - alter Content geht nach links)
			transition('initial => slideLeft', [
				animate(
					'250ms ease-in-out',
					style({
						transform: 'translateX(-100%)',
						opacity: 0,
					})
				),
			]),
			// Animation when sliding out to right (for previous - alter Content geht nach rechts)
			transition('initial => slideRight', [
				animate(
					'250ms ease-in-out',
					style({
						transform: 'translateX(100%)',
						opacity: 0,
					})
				),
			]),
			// Animation when sliding in from right (after slideLeft - neuer Content kommt von rechts)
			transition('slideLeft => initial', [
				style({
					transform: 'translateX(100%)',
					opacity: 0,
				}),
				animate(
					'250ms ease-in-out',
					style({
						transform: 'translateX(0%)',
						opacity: 1,
					})
				),
			]),
			// Animation when sliding in from left (after slideRight - neuer Content kommt von links)
			transition('slideRight => initial', [
				style({
					transform: 'translateX(-100%)',
					opacity: 0,
				}),
				animate(
					'250ms ease-in-out',
					style({
						transform: 'translateX(0%)',
						opacity: 1,
					})
				),
			]),
		]),
	],
})
export class ParameterValueDialogComponent {
	protected readonly layoutService = inject(LayoutService);
	protected readonly dialogRef = inject<MatDialogRef<ParameterValueDialogComponent>>(MatDialogRef);
	protected readonly dateUtil = inject(DateUtil);
	readonly data = inject(MAT_DIALOG_DATA);
	private readonly dialog = inject(MatDialog);
	protected readonly translocoLocale = inject(TranslocoLocaleService);
	private readonly translocoService = inject(TranslocoService);
	private readonly snackbarService = inject(SnackbarService);
	private readonly sessionService = inject(SessionService);
	private readonly tenantUnitReferenceService = inject(TenantUnitReferenceService);
	private readonly tenantUnitUserReferenceService = inject(TenantUnitUserReferenceService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly tenantSelectionValueService = inject(TenantSelectionValueService);
	private readonly tenantPaymentService = inject(TenantPaymentService);
	private readonly tenantPaymentValueService = inject(TenantPaymentValueService);
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantShareValueService = inject(TenantShareValueService);
	private readonly tenantTariffService = inject(TenantTarifService);
	private readonly tenantTariffValueService = inject(TenantTarifValueService);
	private readonly tenantSettingService = inject(TenantSettingService);
	private readonly tenantSettingValueService = inject(TenantSettingValueService);
	private readonly metaDataService = inject(MetadataService);
	private readonly formBuilder = inject(FormBuilder);

	private readonly billingId: string;
	private readonly destroyRef = inject(DestroyRef);
	protected readonly dayjs = dayjs;

	parameterFg: FormGroup;
	minDate = DateUtil.minDate.startOf('day');
	maxDate: Dayjs = dayjs().add(30, 'years').startOf('day');
	today = dayjs().utcOffset(0, true).startOf('day');
	value: number | null = null;
	lockPortalAccessUntil: dayjs.Dayjs = null;

	isAnimating: boolean = false;
	animationState: string = 'initial';
	protected isMobile: boolean;
	protected validFromIsUnlimited: WritableSignal<boolean> = signal(false);
	protected validToIsUnlimited: WritableSignal<boolean> = signal(false);
	protected selectedInstance: WritableSignal<TenantUnitReference> = signal(null);
	protected currentInstances: WritableSignal<TenantUnitReference[]> = signal([]);
	protected currentInstanceUsers: WritableSignal<TenantUnitUserReference[]> = signal([]);
	protected currentParameterType: ParameterValueType;
	parameters: WritableSignal<any[]> = signal([]);
	users: WritableSignal<TenantUnitUserReference[]> = signal(null);
	isUserRelated: WritableSignal<boolean> = signal(false);
	protected parameterSelected: WritableSignal<boolean> = signal(false);
	protected hasValue: WritableSignal<boolean> = signal(false);
	protected hasOptionValue: WritableSignal<boolean> = signal(false);
	currentInstanceIndex: WritableSignal<number> = signal(null);
	selectableValues = signal<any[]>([]);
	selectedSettingType = signal<string>(null);
	protected isEdit = signal(false);
	protected parameterParentId = signal<string>(null);
	protected parameterValueId = signal<string>(null);
	protected parameterUserId = signal<string>(null);
	protected preloadedValue = signal<any>(null);
	parameterUserRoleId = signal<string>(null);

	constructor() {
		const layoutService = this.layoutService;
		const data = this.data;

		this.isMobile = layoutService.isMobile();
		this.billingId = data.billingId;
		this.selectedInstance.set(data.billingInstance);
		this.currentParameterType = data.currentParameter;
		this.hasValue.set(data.hasValue);
		this.isUserRelated.set(data.isUserRelated);
		this.hasOptionValue.set(data.hasOptionValue);
		this.users.set(data.users);
		this.isEdit.set(data.isEdit);
		this.parameterParentId.set(data.parameterParentId);
		this.parameterValueId.set(data.parameterValueId);
		this.parameterUserId.set(data.userId ?? null);
		this.preloadedValue.set(data.preloadedValue ?? null);
		this.lockPortalAccessUntil = data.lockPortalAccessUntil;

		this.createForm();
		this.loadSelectableParameters();

		this.tenantUnitReferenceService
			.readAllTenantUnitReference(this.sessionService.effectiveArea, this.billingId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (currentInstances) => {
					this.currentInstances.set(currentInstances.sort((a, b) => a.number - b.number));
					this.currentInstanceIndex.set(this.currentInstances().findIndex((value1) => value1.exportId === this.selectedInstance().exportId));
				},
			});

		effect(() => {
			if (this.currentInstanceUsers().length === 1 && !this.parameterFg.get('user').value) {
				this.parameterFg.get('user').setValue(this.currentInstanceUsers()[0]);
			}
		});

		// Bei Nutzerauswahl: validFrom/validTo automatisch vom Nutzer übernehmen
		// 1970-01-01 (minDate) und 9999-12-31 (maxDate) sind Sentinel-Werte für "unbegrenzt" → null setzen
		this.parameterFg
			?.get('user')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (selectedUser: TenantUnitUserReference) => {
					if (!this.isEdit()) {
						const from =
							selectedUser?.validFrom && !dayjs(selectedUser.validFrom).isSame(DateUtil.minDate, 'day') ? dayjs(selectedUser.validFrom) : null;
						const to = selectedUser?.validTo && !dayjs(selectedUser.validTo).isSame(DateUtil.maxDate, 'day') ? dayjs(selectedUser.validTo) : null;
						this.parameterFg.get('validFrom').setValue(from);
						this.parameterFg.get('validTo').setValue(to);
					}
				},
			});
	}

	getValueUnitLabel() {
		if (this.currentParameterType === ParameterValueType.SHARE) {
			return this.parameters()?.at(0)?.unit ? this.translocoService.translate('enum.' + this.parameters().at(0).unit) : '';
		}
		return this.translocoLocale.getCurrencySymbol();
	}

	@HostListener('window:keyup.esc')
	onKeyUp() {
		this.close(false);
	}

	private createForm() {
		this.parameterFg = this.formBuilder.group(
			{
				parameter: [{ value: null, disabled: this.isEdit() }, Validators.required],
				validFrom: [{ value: null, disabled: true }],
				validTo: [{ value: null, disabled: true }],
				user: [{ value: '', disabled: true }, this.isUserRelated() ? Validators.required : undefined],
				value: [
					{
						value: null,
						disabled: true,
					},
					this.hasValue() || this.hasOptionValue() ? Validators.required : undefined,
				],
			},
			{
				validators: [
					DateValidator.dateRangeValidator(() => this.validFromIsUnlimited()),
					DateValidator.dateRangeValidator(() => this.validToIsUnlimited()),
					this.usableDateRangeValidator(),
				],
			}
		);

		this.parameterFg.markAllAsTouched();
	}

	private loadSelectableParameters() {
		let loadParameter$: Observable<any>;
		switch (this.currentParameterType) {
			case ParameterValueType.SELECTION:
				loadParameter$ = this.loadSelections();
				break;
			case ParameterValueType.SHARE:
				loadParameter$ = this.loadShares();
				break;
			case ParameterValueType.TARIFF:
				loadParameter$ = this.loadTariffs();
				break;
			case ParameterValueType.PAYMENT:
				loadParameter$ = this.loadPayments();
				break;
			case ParameterValueType.SETTING:
				loadParameter$ = this.loadSettings();
				break;
			default:
				return;
		}
		if (!loadParameter$) {
			this.snackbarService.error(this.translocoService.translate('errors.loadingBillingParameters'));
			return;
		}
		loadParameter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (parameters: any[]) => {
				this.parameters.set(parameters.sort((a, b) => a.name.localeCompare(b.name)));
				if (parameters.length === 1) {
					this.parameterFg.controls.parameter.setValue(parameters[0]);
					this.loadParameter(parameters[0].type);
				} else if (this.parameterParentId()) {
					// Pre-select parameter when plus button clicked on a specific row
					this.setParameterFromEdit();
				}

				if (this.isEdit()) {
					this.setParameterFromEdit();
				}
			},
			error: () => this.snackbarService.error(this.translocoService.translate('errors.loadingBillingParameters')),
		});
	}

	private loadParameterValueToEdit() {
		// If the parent already loaded the value (e.g. from a gantt bar click), use it directly
		if (this.preloadedValue()) {
			this.populateFormFromValue(this.preloadedValue());
			return;
		}
		let loadParameterValue$: Observable<any>;
		switch (this.currentParameterType) {
			case ParameterValueType.SELECTION:
				loadParameterValue$ = this.loadSelectionValue();
				break;
			case ParameterValueType.SHARE:
				loadParameterValue$ = this.loadShareValue();
				break;
			case ParameterValueType.TARIFF:
				loadParameterValue$ = this.loadTariffValue();
				break;
			case ParameterValueType.PAYMENT:
				loadParameterValue$ = this.loadPaymentValue();
				break;
			case ParameterValueType.SETTING:
				loadParameterValue$ = this.loadSettingValue();
				break;
			default:
				return;
		}
		if (!loadParameterValue$) {
			this.snackbarService.error(this.translocoService.translate('errors.loadingBillingParameters'));
			return;
		}
		loadParameterValue$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (parameterValue: any) => this.populateFormFromValue(parameterValue),
			error: () => this.snackbarService.error(this.translocoService.translate('errors.loadingBillingParameters')),
		});
	}

	private populateFormFromValue(parameterValue: any) {
		this.parameterFg.get('validFrom').setValue(parameterValue?.validFrom);
		this.parameterFg.get('validTo').setValue(parameterValue?.validTo);
		if (this.currentParameterType === ParameterValueType.SETTING) {
			this.parameterFg.get('value').setValue(parameterValue?.value?.typeValue);
		} else {
			this.parameterFg.get('value').setValue(parameterValue?.value);
		}
		this.value = parameterValue?.value;
		this.disableValueBeforeLockPortalAccessUntil(parameterValue);
		if (this.isUserRelated() && this.parameterUserId() && !this.parameterFg.get('user').value) {
			const matchingUser = this.currentInstanceUsers().find((u) => u.exportId === this.parameterUserId());
			if (matchingUser) {
				this.parameterFg.get('user').setValue(matchingUser);
			}
		}
	}

	private disableValueBeforeLockPortalAccessUntil(parameterValue: any) {
		const validFrom: Dayjs = parameterValue?.validFrom ? dayjs(parameterValue.validFrom) : null;
		const validTo: Dayjs = parameterValue?.validTo ? dayjs(parameterValue.validTo) : null;
		if (validTo?.isSameOrBefore(this.lockPortalAccessUntil)) {
			this.parameterFg.get('validTo').disable({ emitEvent: false });
			this.parameterFg.get('validFrom').disable({ emitEvent: false });
			this.parameterFg.get('value').disable({ emitEvent: false });
		} else if (validFrom?.isSameOrBefore(this.lockPortalAccessUntil)) {
			this.parameterFg.get('validFrom').disable({ emitEvent: false });
			this.parameterFg.get('value').disable({ emitEvent: false });
		}
	}

	close(saved: boolean, close: boolean = true) {
		if (!saved) {
			this.dialogRef.close(saved);
			return;
		}
		let saveValue$;
		switch (this.currentParameterType) {
			case ParameterValueType.SELECTION:
				saveValue$ = this.createOrUpdateSelectionValue();
				break;
			case ParameterValueType.SHARE:
				saveValue$ = this.createOrUpdateShareValue();
				break;
			case ParameterValueType.TARIFF:
				saveValue$ = this.createOrUpdateTariffValue();
				break;
			case ParameterValueType.PAYMENT:
				saveValue$ = this.createOrUpdatePaymentValue();
				break;
			case ParameterValueType.SETTING:
				saveValue$ = this.createOrUpdateSettingValue();
				break;
			default:
				break;
		}
		if (!saveValue$) {
			this.snackbarService.error(this.translocoService.translate('errors.savingBillingParameters'));
			return;
		}
		saveValue$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: () => {
				if (!this.isEdit()) {
					this.parameterFg.get('validFrom').setValue(null);
					this.validFromIsUnlimited.set(false);
					this.parameterFg.get('validTo').setValue(null);
					this.validToIsUnlimited.set(false);
					this.parameterFg.get('value').setValue(null);
					this.value = null;
				}
				this.parameterFg.markAsPristine();
				this.parameterFg.markAsUntouched();
				this.snackbarService.success(
					`${this.translocoService.translate('billing.parameterValue.' + this.currentParameterType.toUpperCase())} ${this.translocoService.translate('common.saved')}`
				);
				if (close) {
					this.dialogRef.close(true);
				}
			},
			error: () => {
				this.snackbarService.error(
					`${this.translocoService.translate('common.errorWhileSaving')} ${this.translocoService.translate('billing.parameterValue.' + this.currentParameterType.toUpperCase())}`
				);
			},
		});
	}

	getSelectedParameterName(): string {
		if (this.parameters().length == 0) {
			return (
				this.translocoService.translate('billing.noParametersDefined') +
				this.translocoService.translate('billing.parameterValue.' + this.currentParameterType.toUpperCase())
			);
		}
		const selectedParameter = this.parameterFg.get('parameter')?.value;
		return selectedParameter?.name || '';
	}

	getSelectedUserName() {
		const selectedParameter = this.parameterFg.get('user')?.value;
		return selectedParameter?.name || '';
	}

	quicksetDateFrom(formControlName: string, date: Dayjs) {
		this.parameterFg.get(formControlName).setValue(date);
		this.parameterFg.get(formControlName).markAllAsTouched();
	}

	setParameterFromEdit() {
		const parentParameter = this.parameters().find((value1) => value1.id === this.parameterParentId());
		if (!parentParameter) {
			return;
		}
		this.parameterFg.get('parameter').setValue(parentParameter);

		// Always call loadParameter - for Settings pass the type, for others no argument needed
		if (this.currentParameterType === ParameterValueType.SETTING) {
			this.loadParameter(parentParameter.type);
		} else {
			this.loadParameter();
		}
	}

	loadParameter(tenantSettingType: TenantSettingType = undefined) {
		this.parameterSelected.set(true);
		const currentParameter = this.parameterFg.get('parameter')?.value;

		this.minDate = dayjs(currentParameter?.validFrom ?? DateUtil.minDate);
		this.maxDate = dayjs(currentParameter?.validTo ?? this.maxDate);

		if (this.currentParameterType === ParameterValueType.SETTING) {
			this.parameterFg.get('value').setValue(null);
			this.metaDataService
				.getTenantSettingTypes(this.sessionService.effectiveArea)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (tenantSettingTypes) => {
						if (tenantSettingTypes) {
							const tenantSettingWithValue: any = tenantSettingTypes.find((value: any) => value.tenantSettingType === tenantSettingType);
							this.selectedSettingType.set(tenantSettingWithValue.tenantSettingType);
							this.selectableValues.set(tenantSettingWithValue.tenantSettingValueTypes);
						}
					},
					error: () => {
						this.snackbarService.error(this.translocoService.translate('errors.loading'));
					},
				});
		}

		this.parameterFg.get('validFrom').enable();
		this.parameterFg.get('validTo').enable();
		if (this.hasValue() || this.hasOptionValue()) {
			this.parameterFg.get('value').enable();
		}
		if (this.isUserRelated()) {
			this.tenantUnitUserReferenceService
				.readAllTenantUnitUserReference(this.sessionService.effectiveArea, this.billingId, this.selectedInstance().exportId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (allInstanceUsers) => {
						// Nur Nutzer anzeigen die zur Rolle des gewählten Parameters passen (validUserRole = UserRole-ID)
						this.parameterUserRoleId.set(currentParameter?.validUserRole as string | null);
						const filtered = this.parameterUserRoleId()
							? allInstanceUsers.filter((u) => u.userRole?.exportId === this.parameterUserRoleId())
							: allInstanceUsers;
						this.currentInstanceUsers.set(filtered);
						this.parameterFg.get('user').enable();
						if (this.isEdit()) {
							this.loadParameterValueToEdit();
						}
					},
				});
		} else if (this.isEdit()) {
			this.loadParameterValueToEdit();
		}
	}

	private loadSelections() {
		return this.tenantSelectionService.readAllTenantSelection(this.sessionService.effectiveArea, this.billingId);
	}

	private loadShares() {
		return this.tenantShareService.readAllTenantShare(this.sessionService.effectiveArea, this.billingId);
	}

	private loadPayments() {
		return this.tenantPaymentService.readAllTenantPayment(this.sessionService.effectiveArea, this.billingId);
	}

	private loadSettings() {
		return this.tenantSettingService.readAllTenantSetting(this.sessionService.effectiveArea, this.billingId);
	}

	private loadTariffs() {
		return this.tenantTariffService.readAllTenantTarif(this.sessionService.effectiveArea, this.billingId);
	}

	private loadSelectionValue() {
		return this.tenantSelectionValueService.readTenantSelectionValue(this.sessionService.effectiveArea, this.billingId, this.parameterValueId());
	}

	private loadShareValue() {
		return this.tenantShareValueService.readTenantShareValue(this.sessionService.effectiveArea, this.billingId, this.parameterValueId());
	}

	private loadPaymentValue() {
		return this.tenantPaymentValueService.readTenantPaymentValue(this.sessionService.effectiveArea, this.billingId, this.parameterValueId());
	}

	private loadSettingValue() {
		return this.tenantSettingValueService.readTenantSettingValue(this.sessionService.effectiveArea, this.billingId, this.parameterValueId());
	}

	private loadTariffValue() {
		return this.tenantTariffValueService.readTenantTarifValue(this.sessionService.effectiveArea, this.billingId, this.parameterValueId());
	}

	private createBaseValue() {
		const validFrom = this.parameterFg.get('validFrom').value ? dayjs(this.parameterFg.get('validFrom').value) : undefined;
		const validTo = this.parameterFg.get('validTo').value ? dayjs(this.parameterFg.get('validTo').value) : undefined;
		return {
			kindId: this.parameterFg.get('parameter').value.exportId,
			validFrom: validFrom?.format('YYYY-MM-DD'),
			validTo: validTo?.format('YYYY-MM-DD'),
		};
	}

	selectNextInstance() {
		if (this.currentInstanceIndex() === this.currentInstances().length - 1) {
			return;
		}
		if (this.parameterFg.dirty) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: false,
				autoFocus: false,
				restoreFocus: false,
			});
			dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
			dialogRef.componentInstance.confirmMessage = this.translocoService.translate('billing.confirmSwitchBillingInstance');

			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.nextInstance();
				}
			});
		} else {
			this.nextInstance();
		}
	}

	private nextInstance() {
		this.currentInstanceIndex.set(this.currentInstanceIndex() + 1);
		this.selectedInstance.set(this.currentInstances()[this.currentInstanceIndex()]);
		this.setTenantUnitUsers();
		this.animate('slideLeft');
	}

	selectPreviousInstance() {
		if (this.parameterFg.dirty) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: false,
				autoFocus: false,
				restoreFocus: false,
			});
			dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
			dialogRef.componentInstance.confirmMessage = this.translocoService.translate('billing.confirmSwitchBillingInstance');

			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.previousInstance();
				}
			});
		} else {
			this.previousInstance();
		}
	}

	private previousInstance() {
		this.currentInstanceIndex.set(this.currentInstanceIndex() - 1);
		this.selectedInstance.set(this.currentInstances()[this.currentInstanceIndex()]);
		this.setTenantUnitUsers();
		this.animate('slideRight');
	}

	private setTenantUnitUsers() {
		this.parameterValueId.set(null);
		this.currentInstanceUsers.set(
			this.parameterUserRoleId()
				? this.selectedInstance().tenantUnitUserReferences.filter((u) => u.userRole?.exportId === this.parameterUserRoleId())
				: this.selectedInstance().tenantUnitUserReferences
		);
	}

	private animate(direction: 'slideLeft' | 'slideRight') {
		this.isAnimating = true;
		this.animationState = direction;
	}

	onAnimationDone() {
		if (this.animationState !== 'initial') {
			// Nach der Ausgangsanimation: Index ändern und zurück zu initial
			this.animationState = 'initial';
		} else if (this.animationState === 'initial' && this.isAnimating) {
			// Nach der Eingangsanimation: Animation abgeschlossen
			this.isAnimating = false;
		}
	}

	private createOrUpdateValue<T>(config: {
		valueFactory: () => T;
		createFn: (area: number, billingId: string, resourceId: string, value: T) => Observable<any>;
		updateFn: (area: number, billingId: string, resourceId: string, valueId: string, value: T) => Observable<any>;
		useUserId?: boolean;
	}): Observable<any> {
		const newValue = config.valueFactory();
		const resourceId = config.useUserId ? this.parameterFg.get('user').value.id : this.selectedInstance().exportId;
		const area = this.sessionService.effectiveArea;

		if (this.isEdit()) {
			return config.updateFn(area, this.billingId, resourceId, this.parameterValueId(), newValue);
		} else {
			return config.createFn(area, this.billingId, resourceId, newValue);
		}
	}

	private createOrUpdateSelectionValue() {
		return this.createOrUpdateValue<TenantSelectionValue>({
			valueFactory: () => ({
				tenantSelection: this.parameterFg.get('parameter').value,
				...this.createBaseValue(),
			}),
			createFn: (area, billingId, resourceId, value) => this.tenantSelectionValueService.createTenantSelectionValue(area, billingId, resourceId, value),
			updateFn: (area, billingId, resourceId, valueId, value) =>
				this.tenantSelectionValueService.updateTenantSelectionValue(area, billingId, valueId, value),
		});
	}

	private createOrUpdateTariffValue() {
		return this.createOrUpdateValue<TenantTarifValue>({
			valueFactory: () => ({
				tenantTarif: this.parameterFg.get('parameter').value,
				...this.createBaseValue(),
				value: this.parameterFg.get('value').value,
			}),
			createFn: (area, billingId, resourceId, value) => this.tenantTariffValueService.createTenantTarifValue(area, billingId, resourceId, value),
			updateFn: (area, billingId, resourceId, valueId, value) => this.tenantTariffValueService.updateTenantTarifValue(area, billingId, valueId, value),
		});
	}

	private createOrUpdatePaymentValue() {
		return this.createOrUpdateValue<TenantPaymentValue>({
			valueFactory: () => ({
				tenantPayment: this.parameterFg.get('parameter').value,
				...this.createBaseValue(),
				value: this.parameterFg.get('value').value,
			}),
			createFn: (area, billingId, resourceId, value) => this.tenantPaymentValueService.createTenantPaymentValue(area, billingId, resourceId, value),
			updateFn: (area, billingId, resourceId, valueId, value) => this.tenantPaymentValueService.updateTenantPaymentValue(area, billingId, valueId, value),
			useUserId: true,
		});
	}

	private createOrUpdateSettingValue() {
		return this.createOrUpdateValue<TenantSettingValue>({
			valueFactory: () => ({
				tenantSetting: this.parameterFg.get('parameter').value,
				...this.createBaseValue(),
				value: {
					// @ts-ignore // due to manual jackson parsing, other structure is needed
					typeGroup: this.toTypeGroupName(this.selectedSettingType()),
					typeValue: this.parameterFg.get('value').value,
				},
			}),
			createFn: (area, billingId, resourceId, value) => this.tenantSettingValueService.createTenantSettingValue(area, billingId, resourceId, value),
			updateFn: (area, billingId, resourceId, valueId, value) => this.tenantSettingValueService.updateTenantSettingValue(area, billingId, valueId, value),
			useUserId: true,
		});
	}

	private createOrUpdateShareValue() {
		return this.createOrUpdateValue<TenantShareValue>({
			valueFactory: () => ({
				tenantShare: this.parameterFg.get('parameter').value,
				...this.createBaseValue(),
				value: this.parameterFg.get('value').value,
			}),
			createFn: (area, billingId, resourceId, value) => this.tenantShareValueService.createTenantShareValue(area, billingId, resourceId, value),
			updateFn: (area, billingId, resourceId, valueId, value) => this.tenantShareValueService.updateTenantShareValue(area, billingId, valueId, value),
		});
	}

	private toTypeGroupName(input: string): string {
		return input
			.toLowerCase()
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join('')
			.concat('Values');
	}

	private usableDateRangeValidator() {
		return (formGroup: AbstractControl): ValidationErrors | null => {
			const usableFrom = formGroup.get('validFrom')?.value;
			const usableTo = formGroup.get('validTo')?.value;
			const parameterValidFrom = formGroup.get('parameter')?.value?.validFrom;
			const parameterValidTo = formGroup.get('parameter')?.value?.validTo;

			if (!usableFrom && !usableTo) {
				return null;
			}

			const usableFromDate = usableFrom ? dayjs(usableFrom) : null;
			const usableToDate = usableTo ? dayjs(usableTo) : null;
			const parameterValidFromDate = parameterValidFrom ? dayjs(parameterValidFrom) : dayjs(this.minDate);
			const parameterValidToDate = parameterValidTo ? dayjs(parameterValidTo) : dayjs(this.maxDate);
			const errors: any = {};

			const isDateOutOfRange = (date: dayjs.Dayjs) => {
				if (!date) {
					return false;
				}
				const isBefore = parameterValidFromDate && date.isBefore(parameterValidFromDate, 'day');
				const isAfter = parameterValidToDate && date.isAfter(parameterValidToDate, 'day');
				return isBefore || isAfter;
			};
			if (usableFromDate && isDateOutOfRange(usableFromDate)) {
				errors.message = 'errors.dateOutOfRange';
			} else if (usableFromDate?.isSameOrBefore(this.lockPortalAccessUntil) && !formGroup.get('validFrom').disabled) {
				errors.message = 'errors.lockPortalAccessUntil';
			}
			if (usableToDate && isDateOutOfRange(usableToDate)) {
				errors.message = 'errors.dateOutOfRange';
			} else if (usableToDate?.isBefore(this.lockPortalAccessUntil) && !formGroup.get('validTo').disabled) {
				errors.message = 'errors.lockPortalAccessUntil';
			}
			return Object.keys(errors).length ? errors : null;
		};
	}

	protected getUserDatesString(user: TenantUnitUserReference) {
		if (
			user?.validFrom &&
			user?.validTo &&
			DateUtil.minDate.isSame(dayjs(user?.validFrom), 'day') &&
			DateUtil.maxDate.isSame(dayjs(user?.validTo), 'day')
		) {
			return this.translocoService.translate('billing.unlimited');
		} else if (user?.validTo && (!user?.validFrom || DateUtil.minDate.isSame(dayjs(user?.validFrom), 'day'))) {
			return this.translocoService.translate('common.until') + ' ' + this.dateUtil.setDateFormatByActiveLang(user?.validTo, '', false, true);
		} else if (user?.validFrom && (!user?.validTo || DateUtil.maxDate.isSame(dayjs(user?.validTo), 'day'))) {
			return this.translocoService.translate('common.from2') + ' ' + this.dateUtil.setDateFormatByActiveLang(user?.validFrom, '', false, true);
		}
		return `${this.dateUtil.setDateFormatByActiveLang(user?.validFrom, '', false, true)} - ${this.dateUtil.setDateFormatByActiveLang(user?.validTo, '', false, true)}`;
	}

	protected confirmDelete() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('billing.confirmDeleteParameterValue');

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.delete();
			}
		});
	}

	private delete() {
		let deleteValue$: Observable<any>;
		const area = this.sessionService.effectiveArea;
		const parameterValueId = this.parameterValueId();
		switch (this.currentParameterType) {
			case ParameterValueType.SELECTION:
				deleteValue$ = this.tenantSelectionValueService.deleteTenantSelectionValue(area, this.billingId, parameterValueId);
				break;
			case ParameterValueType.SHARE:
				deleteValue$ = this.tenantShareValueService.deleteTenantShareValue(area, this.billingId, parameterValueId);
				break;
			case ParameterValueType.TARIFF:
				deleteValue$ = this.tenantTariffValueService.deleteTenantTarifValue(area, this.billingId, parameterValueId);
				break;
			case ParameterValueType.PAYMENT:
				deleteValue$ = this.tenantPaymentValueService.deleteTenantPaymentValue(area, this.billingId, parameterValueId);
				break;
			case ParameterValueType.SETTING:
				deleteValue$ = this.tenantSettingValueService.deleteTenantSettingValue(area, this.billingId, parameterValueId);
				break;
			default:
				break;
		}
		deleteValue$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (deletedElement) => {
				if (deletedElement) {
					this.snackbarService.success('billing.deleteParameterValueSuccess');
					this.dialogRef.close(true); //trigger reload
					return;
				}
				this.snackbarService.error('billing.deleteParameterValueFail');
			},
			error: () => {
				this.snackbarService.error('billing.deleteParameterValueFail');
			},
		});
	}
}
